import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";
import BackIcon from "../../../assets/images/back-icon.svg";
import { useNavigate } from "react-router-dom";

const AddRole = () => {
  const [roleName, setRoleName] = useState("");
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // State management
  const [permissions, setPermissions] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");

  // Fetch available permissions from API
  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      const response = await fetch(`${apiUrl}renewals/permissions`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const permissionsList = data.data || [];
        setAvailablePermissions(permissionsList);
        
        // Initialize permissions state with all permissions set to false
        const initialPermissions = {};
        permissionsList.forEach(perm => {
          initialPermissions[perm.id] = false;
        });
        setPermissions(initialPermissions);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setError("Failed to fetch permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Add new role
  const addRole = async () => {
    if (!roleName.trim()) {
      setError("Please enter a role name.");
      return;
    }

    // Get selected permissions
    const selectedPermissions = Object.keys(permissions)
      .filter(permId => permissions[permId])
      .map(permId => parseInt(permId));

    if (selectedPermissions.length === 0) {
      setError("Please select at least one permission.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${apiUrl}renewals/roles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: roleName.trim(),
          permissions: JSON.stringify(selectedPermissions),
          lastUpdatedBy: userName || "system",
          isActive: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Role "${roleName}" added successfully!`);
        navigate("/renewals-manage-roles");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to add role");
      }
    } catch (error) {
      console.error("Error adding role:", error);
      setError("Failed to add role");
    } finally {
      setLoading(false);
    }
  };

  // Load user info and permissions on component mount
  useEffect(() => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
    }

    // Fetch permissions
    fetchPermissions();
  }, []);

  const handleRoleNameChange = (e) => {
    setRoleName(e.target.value);
    setError(""); // Clear error when user starts typing
  };

  const handleBack = () => {
    navigate("/renewals-manage-roles");
  };

  const handlePermissionChange = (permissionId) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionId]: !prev[permissionId],
    }));
    setError(""); // Clear error when user selects permissions
  };

  const handleAdd = () => {
    addRole();
  };

  const handleCancel = () => {
    setRoleName("");
    // Reset all permissions to false
    const resetPermissions = {};
    availablePermissions.forEach(perm => {
      resetPermissions[perm.id] = false;
    });
    setPermissions(resetPermissions);
    setError("");
    navigate("/renewals-manage-roles");
  };

  const handleSelectAll = () => {
    const allSelected = availablePermissions.every(perm => permissions[perm.id]);
    const newPermissions = {};
    availablePermissions.forEach(perm => {
      newPermissions[perm.id] = !allSelected;
    });
    setPermissions(newPermissions);
  };

  return (
    <>
      <div className="dashboard-title mb-3">
        <div className="d-flex gap-2">
          <button
            className="btn p-0 me-3 text-dark"
            onClick={handleBack}
            style={{ fontSize: "1.5rem" }}
          >
            <img src={BackIcon} alt="back" />
          </button>
          <h1 className="mb-0">Add Role</h1>
        </div>
        <div className="d-flex align-items-center gap-3">
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body mb-3">
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
            <button
              type="button"
              className="btn-close float-end"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Role Name Input */}
        <div className="mb-4">
          <label htmlFor="roleName" className="form-label fw-medium">
            Role Name *
          </label>
          <input
            type="text"
            className="form-control"
            id="roleName"
            placeholder="Enter role name"
            value={roleName}
            onChange={handleRoleNameChange}
            style={{ maxWidth: "400px" }}
            disabled={loading}
          />
        </div>

        {/* Permissions Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <label className="form-label fw-medium mb-0">
              Permissions *
            </label>
            {availablePermissions.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleSelectAll}
                disabled={permissionsLoading || loading}
              >
                {availablePermissions.every(perm => permissions[perm.id]) 
                  ? "Deselect All" 
                  : "Select All"}
              </button>
            )}
          </div>

          {/* Loading State for Permissions */}
          {permissionsLoading && (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading permissions...</span>
              </div>
              <p className="mt-2 text-muted small">Loading permissions...</p>
            </div>
          )}

          {/* Permissions Grid */}
          {!permissionsLoading && availablePermissions.length > 0 && (
            <div className="row g-3 mb-3">
              {availablePermissions.map((permission) => (
                <div key={permission.id} className="col-md-4">
                  <div className="check-grid">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`permission-${permission.id}`}
                        checked={permissions[permission.id] || false}
                        onChange={() => handlePermissionChange(permission.id)}
                        disabled={loading}
                      />
                      <label
                        className="form-check-label fw-medium"
                        htmlFor={`permission-${permission.id}`}
                      >
                        {permission.permission}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Permissions Available */}
          {!permissionsLoading && availablePermissions.length === 0 && (
            <div className="alert alert-info">
              <p className="mb-0">No permissions available. Please contact administrator.</p>
            </div>
          )}

          {/* Selected Permissions Count */}
          {availablePermissions.length > 0 && (
            <small className="text-muted">
              Selected: {Object.values(permissions).filter(Boolean).length} of {availablePermissions.length} permissions
            </small>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-3">
          <button
            className="btn text-white px-4"
            onClick={handleAdd}
            disabled={loading || permissionsLoading}
            style={{ backgroundColor: "#122066", border: "none" }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              "Add"
            )}
          </button>
          <button
            className="btn btn-outline-secondary px-4"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default AddRole;